import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, filter, map, Observable } from 'rxjs';
import { LobbySignalService } from '@gatho/features/lobby/services/sockets/lobby-data-signal.service';
import { UserService } from '@gatho/services';
import { selectSiteTypes, selectSites, selectAreas, selectRooms, selectDynamicContent, selectUserRoles } from "@gatho/features/lobby/store/lobby.selectors";
import { retrievedSiteTypesList, retrievedSitesList, retrievedAreaList, retrievedRoomsList, retrievedDynamicContentList, userRolesList } from "@gatho/features/lobby/store/lobby.actions";
import { BaseState, BaseTypes } from '../../store/lobby.reducer';
import { PeerVideoAudioControlService } from "@gatho/services/peer-video-audio-control.service";
import { Router } from "@angular/router";
import { SnackBarService } from "@gatho/services/snackbar/snackbar.service";

@Injectable({ providedIn: 'root' })

export class LobbyDataService {

  private _siteType$ = this.store.select(selectSiteTypes);
  private _site$ = this.store.select(selectSites);
  private _area$ = this.store.select(selectAreas);
  private _room$ = this.store.select(selectRooms);
  private _dynamicContent$ = this.store.select(selectDynamicContent);
  private _userRole$ = this.store.select(selectUserRoles);

  public lobbyDataLoaded = false;
  public allPeerVolumeService = new PeerVideoAudioControlService('all');

  private _areaConnectedUsersSource = new BehaviorSubject<AreaConnectedUsers[]>([]);
  public areaConnectedUsers$ = this._areaConnectedUsersSource.asObservable();

  private _roomConnectedUsersSource = new BehaviorSubject<RoomConnectedUsers[]>([]);
  public roomConnectedUsers$ = this._roomConnectedUsersSource.asObservable();

  public allSiteTypes$ = this._siteType$.pipe(
    filter(state => state.dataLoaded),
    map(state => [...state.data ?? []]),
  );

  public allSites$ = this._site$.pipe(
    filter(state => state.dataLoaded),
    map(state => [...state.data ?? []]),
  );

  public allAreas$ = this._area$.pipe(
    filter(state => state.dataLoaded),
    map(state => [...state.data ?? []]),
  );

  public allRooms$ = this._room$.pipe(
    filter(state => state.dataLoaded),
    map(state => [...state.data ?? []]),
  );

  public allDynamicContent$ = this._dynamicContent$.pipe(
    filter(state => state.dataLoaded),
    map(state => [...state.data ?? []]),
  );

  public allUserRoles$ = this._userRole$.pipe(
    filter(state => state.dataLoaded),
    map(state => [...state.data ?? []]),
  );

  constructor(
    private store: Store,
    private lobbySignal: LobbySignalService,
    private userService: UserService,
    private _router: Router,
    private _snackBarService: SnackBarService,
  ) {}

  initDataSubscription() {
    this.lobbySignal.initLobbySignalService(() => this.doInitDataSubscriptions());
  }
  private doInitDataSubscriptions() {
    // There is an all lobby data event that is fired primarily when the backend server first starts
    this.lobbySignal.onAllLobbyData().subscribe(({ siteTypes, sites, areas, dynamicContent }) => {
      if (siteTypes) {
        this.store.dispatch(retrievedSiteTypesList({siteTypes}));
      }
      if (sites) {
        this.store.dispatch(retrievedSitesList({sites}));
      }
      if (areas) {
        this.store.dispatch(retrievedAreaList({areas}));
      }
      if (dynamicContent) {
        this.store.dispatch(retrievedDynamicContentList({dynamicContent}));
      }
      this.lobbyDataLoaded = true;
    });
    // Listen for site types being updated
    this.lobbySignal.onDataSiteTypes().subscribe(siteTypes => {
      this.store.dispatch(retrievedSiteTypesList({siteTypes}));
    });
    // Listen for sites being updated
    this.lobbySignal.onDataSites().subscribe(sites => {
      this.store.dispatch(retrievedSitesList({sites}));
    });
    // Listen for areas being updated
    this.lobbySignal.onDataArea().subscribe(areas => {
      this.store.dispatch(retrievedAreaList({areas}));
    });
    this.lobbySignal.onDataDynamicContent().subscribe(dynamicContent => {
      this.store.dispatch(retrievedDynamicContentList({dynamicContent}));
    });
    this.lobbySignal.onAreaRoomsData().subscribe(rooms => {
      this.store.dispatch(retrievedRoomsList({rooms}));
    });
    // Listen for area connected user counts being updated
    this.lobbySignal.onAreaConnectedUserCount().subscribe(areas => {
      const areaConnectedUsers = areas.map<AreaConnectedUsers>(area => {
        return { areaId: area.uuid, connectedUsers: area.occupiedCount }
      });
      this._areaConnectedUsersSource.next(areaConnectedUsers);
      const roomConnectedUsers = areas.flatMap<RoomConnectedUsers>(area => {
        return area.rooms.map(([id, count]) => {
          return {
            roomId: `${area.uuid}-${id}`,
            connectedUsers: count
          };
        })
      });
      this._roomConnectedUsersSource.next(roomConnectedUsers);
    })
    this.lobbySignal.onUserNotFound().subscribe(() => {
      this._snackBarService.open(
        'Your session has expired. Please login again',
        { panelClass: 'error' }
      )
      this.userService.clearUserData();
      // should we force a page refresh or perform client-side routing
      // window.location.href = `${window.location.origin}/login`;
      this._router.navigate(['/login']);
    })
    // Listen for user roles data being supplied / updated
    this.lobbySignal.onUserRolesData().subscribe((user_roles) => {
      this.store.dispatch(userRolesList({user_roles}));
    })
  }

  private selectStore<T extends BaseTypes>(dataType: DataType): Observable<BaseState<T>>
  {
    switch (dataType) {
      case 'siteType':
        return this._siteType$ as Observable<BaseState<T>>;
      case 'site':
        return this._site$ as Observable<BaseState<T>>;
      case 'area':
        return this._area$ as Observable<BaseState<T>>;
      case 'room':
        return this._room$ as Observable<BaseState<T>>;
      case 'dynamicContent':
        return this._dynamicContent$ as Observable<BaseState<T>>;
      case 'userRole':
        return this._userRole$ as Observable<BaseState<T>>;
      default:
        throw new Error('Unknown store type requested');
    }
  }

  subscribeToStore<T extends BaseTypes>(dataType: DataType, callback: (state: BaseState<T>) => void) {
    const lobbyStore = this.selectStore<T>(dataType);
    return lobbyStore.subscribe(state => callback(state as BaseState<T>));
  }
}

export interface AreaConnectedUsers {
  areaId: string,
  connectedUsers: number
}

export interface RoomConnectedUsers {
  roomId: string;
  connectedUsers: number;
}

export type DataType = "siteType" | "site" | "area" | "room" | "dynamicContent" | "userRole";
